import { FC, HTMLAttributes, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { VodacomRewardCard } from "../VodacomRewardCard/VodacomRewardCard";
import { CoinNumber } from "../../core/CoinNumber/CoinNumber";
import {
  CoinWallet,
  Achievement as AchievementType,
  meGQL,
} from "@onmo/graphql/graphql";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { RankColor } from "../../../constants/user";
import { TierLevel, User } from "../../../models/user";
import { convertAchievement } from "../../../models/achievement";
import { useAppSelector } from "../../hooks";
import { useStyles } from "./DailyRewardCategory.style";
import { useWalletConfig } from "../../../hooks/useWalletConfig";
import { getWalletBalance } from "../../../constants/wallet";
import { useQuery } from "@apollo/client";

interface IDailyRewardCategoryProps {
  wallets: CoinWallet[];
  newAchievements?: AchievementType[];
  tierLevels: TierLevel[];
}

export const DailyRewardCategory: FC<
  HTMLAttributes<HTMLDivElement> & IDailyRewardCategoryProps
> = (props) => {
  const classes = useStyles();
  const { wallets, newAchievements, tierLevels } = props;
  const { t } = useTranslation();
  const { achievements } = useAppSelector((state) => state.user);
  const { walletConfig } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;
  const userRank = useMemo(() => {
    return User.getCurrentRank({
      wallet: wallets,
      tierLevels: tierLevels,
      baseCoinType,
    });
  }, [wallets, tierLevels, baseCoinType]);
  const { data: dataMe } = useQuery(meGQL);

  const achievementsToDisplay = newAchievements || [];

  const achievementPairs = useMemo(() => {
    return achievementsToDisplay.map((newAch) => {
      const oldAch = achievements?.find(
        (a: AchievementType) => a.id === newAch?.id,
      );
      return {
        id: newAch.id,
        oldFullAchievement: oldAch ? convertAchievement(oldAch) : undefined,
        newFullAchievement: convertAchievement(newAch),
      };
    });
  }, [achievementsToDisplay, achievements]);

  const hasAchievements = achievementPairs.some(
    ({ newFullAchievement }) => !!newFullAchievement.achievementImage,
  );

  return (
    <div>
      {userRank?.nextLevel && (
        <div>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            className={classes.title}
          >
            {t("common_Rank")}
          </Typography>
          <VodacomRewardCard
            userRank={userRank}
            url={dataMe?.me?.avatar}
            titleContent={
              <>
                <CoinNumber
                  amount={
                    userRank?.nextLevel.xp -
                    getWalletBalance(
                      wallets ?? [],
                      walletConfig.primaryCurrency.base,
                    )
                  }
                  size={16}
                  type={walletConfig.primaryCurrency.base}
                  reverse
                />
                {t("commom_to_rannk", { rank: userRank?.nextLevel.name })}
              </>
            }
            progress={userRank.levelPercent}
            progressColor={userRank.color || RankColor.UnRanked}
          />
        </div>
      )}
      {userRank && hasAchievements && (
        <div className={classes.section}>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            className={classes.title}
          >
            {t("common_Achievements")}
          </Typography>
          <div className={classes.content}>
            {achievementPairs.map(
              ({ id, oldFullAchievement, newFullAchievement }) => (
                <div key={id}>
                  {newFullAchievement?.achievementImage && (
                    <VodacomRewardCard
                      imageSrc={newFullAchievement.achievementImage}
                      titleContent={t(newFullAchievement?.description || "", {
                        amount: newFullAchievement.nextTierTarget,
                        appName: import.meta.env.VITE_APP_NAME,
                      })}
                      progress={newFullAchievement.progressPercentage}
                      initialProgress={oldFullAchievement?.progressPercentage}
                      progressColor={newFullAchievement.color || ""}
                    />
                  )}
                </div>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};
